import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { cancelTimeout, requestTimeout, TimeoutID } from '../timer';
import { ScrollDirection } from '../types';

export const IS_SCROLLING_DEBOUNCE_INTERVAL = 150;

export interface UseDynamicScrollingProps {
  onScroll?: (offset: number) => void;
}

/**
 * Hook for managing scrolling within the DynamicList.
 */
export const useDynamicScrolling = ({ onScroll: onScrollProp }: UseDynamicScrollingProps) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('forward');
  const [scrollOffset, setScrollOffset] = useState(0);
  const resetIsScrollingTimeoutId = useRef<TimeoutID | null>(null);

  const resetIsScrolling = useCallback(() => {
    resetIsScrollingTimeoutId.current = null;
    setIsScrolling(false);
  }, [setIsScrolling]);

  const cancelResetIsScrolling = useCallback(() => {
    if (resetIsScrollingTimeoutId.current !== null) {
      cancelTimeout(resetIsScrollingTimeoutId.current);
    }
  }, []);

  /** Cancel the existing timout and start a new one. */
  const resetIsScrollingDebounced = useCallback(() => {
    cancelResetIsScrolling();
    resetIsScrollingTimeoutId.current = requestTimeout(
      resetIsScrolling,
      IS_SCROLLING_DEBOUNCE_INTERVAL,
    );
  }, [cancelResetIsScrolling, resetIsScrolling]);

  const onScroll = useCallback(
    (event: SyntheticEvent<HTMLDivElement>): void => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;

      if (scrollOffset !== scrollTop) {
        // Prevent Safari's elastic scrolling from causing visual shaking when scrolling past bounds.
        const newScrollOffset = Math.max(0, Math.min(scrollTop, scrollHeight - clientHeight));
        setScrollOffset(newScrollOffset);
        setIsScrolling(true);
        resetIsScrollingDebounced();
        setScrollDirection(scrollOffset < newScrollOffset ? 'forward' : 'backward');
        onScrollProp?.(newScrollOffset);
      }
    },
    [onScrollProp, resetIsScrollingDebounced, scrollOffset],
  );

  useEffect(() => cancelResetIsScrolling, [cancelResetIsScrolling]);

  return {
    isScrolling,
    scrollDirection,
    scrollOffset,
    onScroll,
  };
};
